import React, { useState, useEffect } from 'react';
import { Row, Col, Card, CardHeader, CardBody, Form, Label, Input, FormGroup, Button, FormFeedback } from 'reactstrap';
import { getAccountData, AccountData, updateAccountData } from 'features/matriculates/matriculatesAPI';
import { useForm } from 'hooks/useForm';
import { useAlert } from 'hooks';
import AlertModal from 'components/AlertModal';
import validator from 'helpers/validator';
import AccountRequestsList from './AccountRequestsList';
import { getPartidos } from '../../features/partidos/partidosAPI';
import { Partidos } from 'types/Partidos';
import { getLocalidades } from '../../features/localidades/localidadesAPI';
import { Localidades } from 'types/Localidades';

interface ValidationErrors {
  celular_codigo?: string;
  celular_numero?: string;
  domicilio_particular_telefono_codigo?: string;
  domicilio_particular_telefono_numero?: string;
  domicilio_legal_telefono_codigo?: string;
  domicilio_legal_telefono_numero?: string;
}

interface Props {
  title?: string;
}

export default function CuentaForm({ title = 'Mi Cuenta' }: Props) {
  const [isLoading, setIsLoading] = useState(true);
  const [especialidades, setEspecialidades] = useState<AccountData['especialidades']>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});

  const [showAlert, message, alert] = useAlert(false, '');

  const [disableSelect, setDisableSelect] = useState(true);
  const [particularPartidos, setParticularPartidos] = useState<Partidos[]>([]);
  const [particularLocalidades, setParticularLocalidades] = useState<
    Localidades[]
  >([]);
  const [legalPartidos, setLegalPartidos] = useState<Partidos[]>([]);
  const [legalLocalidades, setLegalLocalidades] = useState<Localidades[]>([]);

  const [originalData, setOriginalData] = useState<Omit<AccountData, | 'especialidades'>>();
  const [data, handleChange, setData] = useForm<Omit<AccountData, | 'especialidades'>>({
    nombre: '',
    apellido: '',
    tipo_doc: '',
    nro_doc: '',
    matricula: '',
    email: '',
    celular_codigo: '',
    celular_numero: '',
    domicilio_particular: {
      calle: '',
      numero: '',
      piso: '',
      depto: '',
      cp: '',
      localidad_id: '',
      localidad: '',
      partido_id: '',
      partido: '',
      telefono_codigo: '',
      telefono_numero: '',
    },
    domicilio_legal: {
      calle: '',
      numero: '',
      piso: '',
      depto: '',
      cp: '',
      localidad_id: '',
      localidad: '',
      partido_id: '',
      partido: '',
      telefono_codigo: '',
      telefono_numero: '',
    },
  });

  useEffect(() => {
    setIsLoading(true);

    const loadData = async () => {
      try {
        const response = await getAccountData();

        setData(response);
        setOriginalData(response);
        setEspecialidades(response.especialidades);

        const provinciaId = 1; // Buenos Aires
        const partidosResponse = await getPartidos(provinciaId);
        if (partidosResponse) {
          setParticularPartidos(partidosResponse);
          const partidosFiltrados = partidosResponse.filter(
            partido => ![2698, 2833].includes(partido.MTR038_ID_PARTIDO), // Filtramos para excluir CABA y "No Informado"
          );
          setLegalPartidos(partidosFiltrados);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  useEffect(() => {
    if (data.domicilio_particular.partido_id) {
      loadLocalidades('dom_part_part', +data.domicilio_particular.partido_id);
    }
  }, [data.domicilio_particular.partido_id]);

  useEffect(() => {
    if (data.domicilio_legal.partido_id) {
      loadLocalidades('dom_legal_part', +data.domicilio_legal.partido_id);
    }
  }, [data.domicilio_legal.partido_id]);

  const handleCancelation = () => {
    setErrors({});
    if (originalData) {
      setData(originalData);
      setIsEditing(false);
      return;
    }

    getAccountData()
      .then(response => {
        setData(response);
        setOriginalData(response);
        setEspecialidades(response.especialidades);
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setIsEditing(false);
        setIsLoading(false);
      });
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!validator.isCodigoArea(data.celular_codigo)) {
      newErrors.celular_codigo = 'El código de área debe tener entre 2 y 4 dígitos';
    }
    if (!validator.isNumeroTelefono(data.celular_numero)) {
      newErrors.celular_numero = 'El número debe tener entre 6 y 8 dígitos';
    }
    if (!validator.isCodigoArea(data.domicilio_particular.telefono_codigo)) {
      newErrors.domicilio_particular_telefono_codigo = 'El código de área debe tener entre 2 y 4 dígitos';
    }
    if (!validator.isNumeroTelefono(data.domicilio_particular.telefono_numero)) {
      newErrors.domicilio_particular_telefono_numero = 'El número debe tener entre 6 y 8 dígitos';
    }
    if (!validator.isCodigoArea(data.domicilio_legal.telefono_codigo)) {
      newErrors.domicilio_legal_telefono_codigo = 'El código de área debe tener entre 2 y 4 dígitos';
    }
    if (!validator.isNumeroTelefono(data.domicilio_legal.telefono_numero)) {
      newErrors.domicilio_legal_telefono_numero = 'El número debe tener entre 6 y 8 dígitos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) {
      alert(true, 'Por favor, corrija los errores en el formulario antes de guardar.');
      return;
    }

    updateAccountData(data)
      .then(response => {
        if (!response || response < 1) {
          return;
        }

        alert(
          true,
          `Se envió la solicitud #${ response } para la actualización de datos.` +
          ' Recibirá un correo electrónico cuando la misma sea revisada con la resolución.');
      })
      .catch(err => {
        alert(true, 'No se pudo actualizar la información');
        console.error(err);
      })
      .finally(() => {
        setIsEditing(false);
        setIsLoading(false);
      });
  };

  const loadLocalidades = async (name: string, value: number) => {
    setDisableSelect(true);
    try {
      const localidadesResponse = await getLocalidades(value);
      if (localidadesResponse) {
        switch (name) {
          case 'dom_part_part':
            setParticularLocalidades(localidadesResponse);
            break;
          case 'dom_legal_part':
            const localidadesFiltrados = localidadesResponse.filter(
              localidad => ![1083].includes(localidad.MTR008_ID_LOCALIDAD), // Filtramos para excluir "No Informada"
            );
            setLegalLocalidades(localidadesFiltrados);
            break;
        }
        setDisableSelect(false);
      } else {
        alert('Error al cargar las localidades. Por favor intente seleccionar el partido nuevamente.');
      }
    } catch (err) {
      alert('Error al cargar las localidades. Por favor intente seleccionar el partido nuevamente.');
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md={12}>
          <Card>
            <CardHeader>
              <Row>
                <Col style={{ fontSize: '1.5rem', marginTop: '10px' }} md={8}>{ title }</Col>
                <Col md={4} className="text-right">
                  {
                    isEditing
                      ? <>
                          <Button color="danger" onClick={ handleSave }>
                            Guardar
                          </Button>
                          <Button onClick={ handleCancelation }>
                            Cancelar
                          </Button>
                        </>
                      : <Button onClick={ () => setIsEditing(true) }>
                          Editar
                        </Button>
                  }
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {
                isLoading && (
                  <div className="text-center">
                    <p>Cargando...</p>
                  </div>
                )
              }
              {
                !isLoading && !data && (
                  <div className="text-center">
                    <p>No se pudo obtener la información de la cuenta.</p>
                  </div>
                )
              }
              {
                !isLoading && data && (
                  <Form>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label for="nombre">Nombre</Label>
                          <Input
                            type="text"
                            name="nombre"
                            id="nombre"
                            value={ data.nombre }
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label for="apellido">Apellido</Label>
                          <Input
                            type="text"
                            name="apellido"
                            id="apellido"
                            value={ data.apellido }
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label for="matricula">Matrícula</Label>
                          <Input
                            name="matricula"
                            id="matricula"
                            value={ data.matricula }
                            readOnly
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup>
                          <Label for="doc">DNI</Label>
                          <Input name="doc" id="doc" value={ data.nro_doc } readOnly />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="6">
                        <FormGroup>
                          <Label for="email">Correo electrónico</Label>
                          <Input
                            type="email"
                            name="email"
                            id="email"
                            value={ data.email }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                          />
                        </FormGroup>
                      </Col>
                      <Col md="2">
                        <FormGroup>
                          <Label for="celular_codigo">Código Área Celular</Label>
                          <Input
                            name="celular_codigo"
                            id="celular_codigo"
                            value={ data.celular_codigo }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                            invalid={ !!errors.celular_codigo }
                          />
                          <FormFeedback>{ errors.celular_codigo }</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for="celular_numero">Número Celular</Label>
                          <Input
                            name="celular_numero"
                            id="celular_numero"
                            value={ data.celular_numero }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                            invalid={ !!errors.celular_numero }
                          />
                          <FormFeedback>{ errors.celular_numero }</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>

                    <p>
                      NOTA: Artículo 75 Cod. Civil y Comercial de la Nación las personas pueden elegir un
                      domicilio para el ejercicio de los derechos y obligaciones, pudiendo constituir domicilio
                      electrónico en el que se tengan por eficaces todas las notificaciones y comunicaciones
                    </p>

                    <hr className="my-4" />

                    <h5>Domicilio Particular</h5>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label for="dom_part_calle">Calle</Label>
                          <Input
                            name="domicilio_particular.calle"
                            id="dom_part_calle"
                            value={ data.domicilio_particular.calle }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                          />
                        </FormGroup>
                      </Col>
                      <Col md="2">
                        <FormGroup>
                          <Label for="dom_part_num">Número</Label>
                          <Input
                            name="domicilio_particular.numero"
                            id="dom_part_num"
                            value={ data.domicilio_particular.numero }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label for="dom_part_piso">Piso</Label>
                          <Input
                            name="domicilio_particular.piso"
                            id="dom_part_piso"
                            value={ data.domicilio_particular.piso }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label for="dom_part_depto">Depto</Label>
                          <Input
                            name="domicilio_particular.depto"
                            id="dom_part_depto"
                            value={ data.domicilio_particular.depto }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label for="dom_part_part">Partido</Label>
                          <Input
                            id='dom_part_part'
                            name='domicilio_particular.partido_id'
                            type='select'
                            className='custom-select form-control'
                            value={ data.domicilio_particular.partido_id ?? '' }
                            onChange={ handleChange }
                            disabled={ disableSelect || !isEditing || !particularPartidos.length }
                            readOnly={ !isEditing }
                          >
                            <option value=''>Seleccione una opción...</option>
                            {particularPartidos.map((item: any) => {
                              return (
                                <option
                                  key={item.MTR038_ID_PARTIDO}
                                  value={item.MTR038_ID_PARTIDO}
                                >
                                  {item.MTR038_D_PARTIDO}
                                </option>
                              );
                            })}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for="dom_part_loc">Localidad</Label>
                          <Input
                            id='dom_part_loc'
                            name='domicilio_particular.localidad_id'
                            type='select'
                            className='custom-select form-control'
                            value={data.domicilio_particular.localidad_id ?? ''}
                            onChange={handleChange}
                            disabled={disableSelect || !isEditing || !particularLocalidades.length}
                            readOnly={ !isEditing }
                          >
                            <option value=''>Seleccione una opción...</option>
                            {particularLocalidades.map((item: any) => {
                              return (
                                <option
                                  key={item.MTR008_ID_LOCALIDAD}
                                  value={item.MTR008_ID_LOCALIDAD}
                                >
                                  {item.MTR008_D_LOCALIDAD}
                                </option>
                              );
                            })}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for="dom_part_cp">Código Postal</Label>
                          <Input
                            name="domicilio_particular.cp"
                            id="dom_part_cp"
                            value={ data.domicilio_particular.cp }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="2">
                        <FormGroup>
                          <Label for="dom_part_telefono_codigo">Código Área Teléfono</Label>
                          <Input
                            name="domicilio_particular.telefono_codigo"
                            id="dom_part_telefono_codigo"
                            value={ data.domicilio_particular.telefono_codigo }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                            invalid={ !!errors.domicilio_particular_telefono_codigo }
                          />
                          <FormFeedback>{ errors.domicilio_particular_telefono_codigo }</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for="dom_part_telefono_numero">Número Teléfono</Label>
                          <Input
                            name="domicilio_particular.telefono_numero"
                            id="dom_part_telefono_numero"
                            value={ data.domicilio_particular.telefono_numero }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                            invalid={ !!errors.domicilio_particular_telefono_numero }
                          />
                          <FormFeedback>{ errors.domicilio_particular_telefono_numero }</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>

                    <hr className="my-4" />

                    <h5>Domicilio Legal</h5>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label for="dom_legal_calle">Calle</Label>
                          <Input
                            name="domicilio_legal.calle"
                            id="dom_legal_calle"
                            value={ data.domicilio_legal.calle }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                          />
                        </FormGroup>
                      </Col>
                      <Col md="2">
                        <FormGroup>
                          <Label for="dom_legal_num">Número</Label>
                          <Input
                            name="domicilio_legal.numero"
                            id="dom_legal_num"
                            value={ data.domicilio_legal.numero }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label for="dom_legal_piso">Piso</Label>
                          <Input
                            name="domicilio_legal.piso"
                            id="dom_legal_piso"
                            value={ data.domicilio_legal.piso }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                          />
                        </FormGroup>
                      </Col>
                      <Col md="3">
                        <FormGroup>
                          <Label for="dom_legal_depto">Depto</Label>
                          <Input
                            name="domicilio_legal.depto"
                            id="dom_legal_depto"
                            value={ data.domicilio_legal.depto }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="4">
                        <FormGroup>
                          <Label for="dom_legal_part">Partido</Label>
                          <Input
                            id='dom_legal_part'
                            name='domicilio_legal.partido_id'
                            type='select'
                            className='custom-select form-control'
                            value={ data.domicilio_legal.partido_id ?? '' }
                            onChange={ handleChange }
                            disabled={ disableSelect || !isEditing || !legalPartidos.length }
                            readOnly={ !isEditing }
                          >
                            <option value=''>Seleccione una opción...</option>
                            {legalPartidos.map((item: any) => {
                              return (
                                <option
                                  key={item.MTR038_ID_PARTIDO}
                                  value={item.MTR038_ID_PARTIDO}
                                >
                                  {item.MTR038_D_PARTIDO}
                                </option>
                              );
                            })}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for="dom_legal_loc">Localidad</Label>
                          <Input
                            id='dom_legal_loc'
                            name='domicilio_legal.localidad_id'
                            type='select'
                            className='custom-select form-control'
                            value={data.domicilio_legal.localidad_id ?? ''}
                            onChange={handleChange}
                            disabled={disableSelect || !isEditing || !legalLocalidades.length}
                            readOnly={ !isEditing }
                          >
                            <option value=''>Seleccione una opción...</option>
                            {legalLocalidades.map((item: any) => {
                              return (
                                <option
                                  key={item.MTR008_ID_LOCALIDAD}
                                  value={item.MTR008_ID_LOCALIDAD}
                                >
                                  {item.MTR008_D_LOCALIDAD}
                                </option>
                              );
                            })}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for="dom_legal_cp">Código Postal</Label>
                          <Input
                            name="domicilio_legal.cp"
                            id="dom_legal_cp"
                            value={ data.domicilio_legal.cp }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col md="2">
                        <FormGroup>
                          <Label for="dom_legal_telefono_codigo">Código Área Teléfono</Label>
                          <Input
                            name="domicilio_legal.telefono_codigo"
                            id="dom_legal_telefono_codigo"
                            value={ data.domicilio_legal.telefono_codigo }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                            invalid={ !!errors.domicilio_legal_telefono_codigo }
                          />
                          <FormFeedback>{ errors.domicilio_legal_telefono_codigo }</FormFeedback>
                        </FormGroup>
                      </Col>
                      <Col md="4">
                        <FormGroup>
                          <Label for="dom_legal_telefono_numero">Número Teléfono</Label>
                          <Input
                            name="domicilio_legal.telefono_numero"
                            id="dom_legal_telefono_numero"
                            value={ data.domicilio_legal.telefono_numero }
                            readOnly={ !isEditing }
                            onChange={ handleChange }
                            invalid={ !!errors.domicilio_legal_telefono_numero }
                          />
                          <FormFeedback>{ errors.domicilio_legal_telefono_numero }</FormFeedback>
                        </FormGroup>
                      </Col>
                    </Row>
                    <p>
                      NOTA: Artículo 74 Cód. Civil y Comercial de la Nación: El domicilio legal es el lugar
                      donde la presume sin admitir prueba en contra, que una persona reside de manera permanente
                      para el ejercicio de sus derechos y el cumplimiento de sus obligaciones.
                    </p>
                    <hr className="my-4" />

                    <h5>Especialidades</h5>
                    {
                      especialidades && especialidades.length > 0
                        ? (
                            <ul>
                              {
                                especialidades.map(esp => (
                                  <li key={esp.id}>{esp.descripcion}</li>
                                ))
                              }
                            </ul>
                          )
                        : <p>No tiene especialidades declaradas.</p>
                    }
                  </Form>
                )
              }
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <AccountRequestsList />
        </Col>
      </Row>
      <AlertModal isOpen={ showAlert } message={ message } onClose={ () => alert(false) } />
    </div>
  );
}
